import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Edit } from "lucide-react";
import StatusBadge from "../common/StatusBadge";
import type { Constante } from "../../types";

interface ConstanteViewBaseProps {
  role: "admin" | "doctor";
  constante: Constante;
}

const ConstanteViewBase: React.FC<ConstanteViewBaseProps> = ({ role, constante }) => {
  const navigate = useNavigate();

  // Construction du nom complet à partir des données disponibles
  const fullName = constante.patientFullName ||
    (constante.patient
      ? `${constante.patientFullName || ''} ${constante.patientFullName || ''}`.trim()
      : `${constante.firstName || ''} ${constante.lastName || ''}`.trim()) ||
    'Patient Inconnu';

  // Gestion de la taille (priorité aux anciennes propriétés pour compatibilité)
  const heightValue = constante.taille || constante.height;
  const heightInCm = heightValue
    ? (typeof heightValue === 'string' ? parseFloat(heightValue) :
      heightValue < 10 ? heightValue * 100 : heightValue)
    : null;

  // Formatage de la date d'enregistrement
  const recordedDate = constante.recordedAt
    ? new Date(constante.recordedAt).toLocaleDateString('fr-FR')
    : 'Date non disponible';

  // Formatage du motif (priorité à l'ancienne propriété)
  const formatReason = (reason?: string) => {
    if (!reason) return 'Non spécifié';

    const reasonMap: { [key: string]: string } = {
      'PRE_EMPLOYMENT_VISIT': 'Visite de pré-embauche',
      'SPECIALIST_CONSULTATION': 'Consultation spécialisée',
      'GENERAL_CONSULTATION': 'Consultation générale',
      'PEDIATRIC_CONSULTATION': 'Consultation pédiatrique',
      'RESPIRATORY_PHYSIOTHERAPY': 'Kinésithérapie respiratoire',
    };

    return reasonMap[reason] || reason;
  };

  const displayReason = constante.motif || formatReason(constante.reason);

  return (
    <div
      className={`min-h-screen ${role === "admin" ? "bg-gradient-to-br from-slate-50 via-white to-slate-100" : "bg-gray-50"
        } py-8 px-4 sm:px-6 lg:px-8`}
    >
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div
            className={`p-6 md:p-8 ${role === "admin"
                ? "bg-gradient-to-r from-blue-500 to-purple-600"
                : "bg-gradient-to-r from-indigo-700 to-indigo-600"
              } text-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4`}
          >
            <div className="flex items-center">
              <button
                onClick={() => navigate(`/${role}/dashboard/constante`)}
                className="flex items-center gap-2 text-white hover:text-gray-200 transition duration-200"
              >
                <ArrowLeft size={20} />
                <span className="text-lg">Retour</span>
              </button>
            </div>
            <div className="flex flex-col items-center">
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold">Fiche des Constantes Médicales</h2>
              {constante.status && <StatusBadge status={constante.status} role={role} />}
            </div>
            <button
              onClick={() => navigate(`/${role}/dashboard/constante/edit/${constante.id}`, { state: { constante } })}
              className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-2.5 rounded-lg transition duration-200 shadow-md"
            >
              <Edit size={18} />
              <span>Modifier</span>
            </button>
          </div>
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 pb-6 border-b border-gray-200">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800">{fullName}</h3>
              <div className="flex flex-col items-end gap-2 mt-2 md:mt-0">
                {/* <span className="text-sm font-medium text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
                  ID: {constante.id}
                </span> */}
                <span className="text-sm text-gray-600">
                  Enregistré le: {recordedDate}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-indigo-50 rounded-xl p-6 space-y-6">
                <h4 className="text-xl font-semibold text-indigo-800 border-b border-indigo-100 pb-3">
                  Informations Générales
                </h4>
                <div className="space-y-5">
                  {heightInCm && (
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500 mb-1">Taille</p>
                      <p className="font-medium text-lg">{heightInCm} cm</p>
                    </div>
                  )}
                  {(constante.patient?.phone || constante.phone) && (
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500 mb-1">Téléphone</p>
                      <p className="font-medium text-lg">{constante.patient?.phone || constante.phone}</p>
                    </div>
                  )}
                  {displayReason && (
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500 mb-1">Motif</p>
                      <p className="font-medium text-lg">{displayReason}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-indigo-50 rounded-xl p-6 space-y-6">
                <h4 className="text-xl font-semibold text-indigo-800 border-b border-indigo-100 pb-3">
                  Valeurs Médicales
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(constante.temperature || constante.temperature) && (
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500 mb-1">Température</p>
                      <p className="font-medium text-lg text-indigo-700">{constante.temperature} °C</p>
                    </div>
                  )}
                  {(constante.poids || constante.weight) && (
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500 mb-1">Poids</p>
                      <p className="font-medium text-lg text-indigo-700">
                        {constante.poids || constante.weight} kg
                      </p>
                    </div>
                  )}
                  {(constante.tensionArterielle || constante.bloodPressure) && (
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500 mb-1">Tension Artérielle</p>
                      <p className="font-medium text-lg text-indigo-700">
                        {constante.tensionArterielle || constante.bloodPressure} mmHg
                      </p>
                    </div>
                  )}
                  {(constante.frequenceCardiaque || constante.heartRate) && (
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500 mb-1">Fréquence Cardiaque</p>
                      <p className="font-medium text-lg text-indigo-700">
                        {constante.frequenceCardiaque || constante.heartRate} bpm
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
              {(constante.doctor || constante.doctorName) && (
                <div className="bg-indigo-50 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-indigo-800 border-b border-indigo-100 pb-3 mb-4">
                    Médecin Traitant
                  </h4>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <p className="font-medium text-lg">
                      {constante.doctor
                        ? `Dr. ${constante.doctor.firstName} ${constante.doctor.lastName}`
                        : constante.doctorName
                      }
                    </p>
                    {constante.doctor?.specialty && (
                      <p className="text-sm text-gray-600 mt-1">{constante.doctor.specialty}</p>
                    )}
                  </div>
                </div>
              )}
              <div className="bg-indigo-50 rounded-xl p-6">
                <h4 className="text-xl font-semibold text-indigo-800 border-b border-indigo-100 pb-3 mb-4">
                  Statut de la Fiche
                </h4>
                <div className="flex items-center gap-4">
                  {constante.status && <StatusBadge status={constante.status} role={role} />}
                  {constante.status === "PENDING" && (
                    <span className="text-sm text-gray-500">En attente de validation</span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-12 flex flex-col sm:flex-row justify-end gap-4">
              <button
                onClick={() => navigate(`/${role}/dashboard/constante`)}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition duration-200 shadow-sm"
              >
                <ArrowLeft size={18} />
                Retour à la liste
              </button>
              <button
                onClick={() => navigate(`/${role}/dashboard/constante/edit/${constante.id}`, { state: { constante } })}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition duration-200 shadow-md"
              >
                <Edit size={18} />
                Modifier les données
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstanteViewBase;